import * as is from 'utilities/is';
import { LAYOUT_ORIENTATION } from 'constants/layout';
import { StackableLayout } from './stackable_layout';
import { sum, normalize, denormalize, inverseNormalize } from 'utilities/math';

export class ProportionalStackableLayout extends StackableLayout {
    constructor(...args) {
        super(...args);
        this.computePhysicalDimensions();
    }

    // determine the size of each child element, relative to any children elements with defined aspect ratios
    // determine this layout's aspect ratio relative to the size of child elements
    computeLayout() {
        this.makeStackable();

        const sourceAspect = this.children.reduce((source, c) => c.aspect ? c.aspect + source : source, 0); 
        
        if (this.orientation === LAYOUT_ORIENTATION.HORIZONTAL) {
            const baseWidth = inverseNormalize(sourceAspect),
                proportionWidths = this.children.map(
                    c => (
                        c.aspect ? inverseNormalize(c.aspect) : normalize(c.width) * baseWidth
                    )
                ),
                totalWidth = proportionWidths.reduce(sum);

            let left = 0;

            this.children.forEach((child, index) => {
                const width = proportionWidths[index] / totalWidth * 100;
                child.width = width;
                child.left = left;
                child.top = 0;
                left += width;
            });

            this.aspect = inverseNormalize(totalWidth);
        } else if (this.orientation === LAYOUT_ORIENTATION.VERTICAL) {
            const baseHeight = normalize(sourceAspect),
                proportionHeights = this.children.map(
                    c => (
                        c.aspect ? normalize(c.aspect) : normalize(c.height) * baseHeight
                    )
                ),
                totalHeight = proportionHeights.reduce(sum);

            let top = 0;

            this.children.forEach((child, index) => {
                const height = denormalize(proportionHeights[index] / totalHeight);
                child.height = height;
                child.top = top;
                child.left = 0;
                top += height;
            });

            this.aspect = denormalize(totalHeight);
        }
    }

    // determine physical size of layout and children using child element with defined physical dimensions
    // physical size is used when scaling annotation content areas
    computePhysicalDimensions() {
        const sourcePhysicalWidth = this.children.filter(c => c.physicalWidth).map(c => c.physicalWidth).reduce(sum),
            sourcePhysicalHeight = this.children.filter(c => c.physicalHeight).map(c => c.physicalHeight).reduce(sum);

        if (this.orientation === LAYOUT_ORIENTATION.HORIZONTAL) {
            const uncomputed = this.children.filter(c => !c.physicalWidth);

            uncomputed.forEach(c => {
                c.physicalWidth = c.config.width / 100 * sourcePhysicalWidth;
                c.physicalHeight = sourcePhysicalHeight;
            });

            this.physicalWidth = (
                uncomputed
                    .reduce((total, c) => total + c.physicalWidth, sourcePhysicalWidth)
            );
            this.physicalHeight = sourcePhysicalHeight;
        } else if (this.orientation === LAYOUT_ORIENTATION.VERTICAL) {
            const uncomputed = this.children.filter(c => !c.physicalHeight);

            uncomputed.forEach(c => {
                c.physicalWidth = sourcePhysicalWidth;
                c.physicalHeight = c.config.height / 100 * sourcePhysicalHeight;
            });

            this.physicalWidth = sourcePhysicalWidth;
            this.physicalHeight = (
                uncomputed
                    .reduce((total, c) => total + c.physicalHeight, sourcePhysicalHeight)
            );
        }

        if (this.root) {
            this.maxPhysicalWidth = this.physicalWidth;
            this.maxPhysicalHeight = this.physicalHeight;
        }
    }

    static isProportional({ aspect, root = false, children = [] }) {
        return (
            (aspect && !root) ||
            (
                !is.empty(children) && 
                children.some(ProportionalStackableLayout.isProportional)
            )
        );
    }
}